import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * shadcn/ui style class-name helper.
 *  - clsx: conditional join
 *  - twMerge: dedupe conflicting Tailwind utility classes
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Format a number into NT$ x,xxx style. */
export function formatCurrency(n: number | null | undefined, prefix = ''): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${prefix}${Math.round(n).toLocaleString('en-US')}`;
}

/** Format a Date / ISO string. Returns '—' if falsy. */
export function formatDate(input: string | Date | null | undefined, fmt: 'date' | 'datetime' = 'date'): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  if (fmt === 'date') return `${yyyy}-${mm}-${dd}`;
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

/** Lightweight relative-time formatter (剛剛 / N 分前 / N 小時前 / N 天前). */
export function timeAgo(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return '剛剛';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} 分前`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} 小時前`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)} 天前`;
  return formatDate(d, 'date');
}

/** First-letter avatar helper (Chinese: takes 1st char, Latin: 1st letter). */
export function avatarText(name?: string | null): string {
  if (!name) return '?';
  return name.trim().slice(0, 1).toUpperCase();
}

/** Pure copy-on-write update for nested arrays/objects. Never mutate originals. */
export function updateAt<T>(arr: readonly T[], index: number, updater: (item: T) => T): T[] {
  return arr.map((item, i) => (i === index ? updater(item) : item));
}

export function removeAt<T>(arr: readonly T[], index: number): T[] {
  return arr.filter((_, i) => i !== index);
}
